import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil, debounceTime, filter } from 'rxjs/operators';
import { OrderService } from '../../services/order.service';
import { SignalRService, OrderStatusUpdate, ScanActivityBroadcast } from '../../services/signalr.service';
import {
  OrderSummaryDto,
  OrderDetailDto,
  ParcelDto,
  PaginatedResponse,
  GetOrdersParams,
  WorkflowStatus
} from '../../../models/order.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideSearch, lucideChevronRight, lucideChevronLeft, lucideChevronDown,
  lucideDownload, lucideEye, lucideLoader, lucideMinus, lucideActivity,
  lucideX, lucideRefreshCw, lucideScanLine
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
  EmptyStateComponent, SlimProgressBarComponent, SkeletonComponent
} from '@shared/ui/components';

@Component({
  selector: 'app-order-list',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, DatePipe,
    ...HlmCardImports, HlmButton, HlmInput,
    NgIcon, HlmIcon,
    PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
    EmptyStateComponent, SlimProgressBarComponent, SkeletonComponent,
  ],
  providers: [provideIcons({
    lucideSearch, lucideChevronRight, lucideChevronLeft, lucideChevronDown,
    lucideDownload, lucideEye, lucideLoader, lucideMinus, lucideActivity,
    lucideX, lucideRefreshCw, lucideScanLine
  })],
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.css']
})
export class OrderListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  ordersResponse: PaginatedResponse<OrderSummaryDto> | null = null;
  isLoading: boolean = false;
  errorMessage: string | null = null;
  highlightedOrderIds: Set<string> = new Set(); // Track highlighted orders
  
  // Expandable rows functionality
  expandedOrders: Set<number> = new Set(); // Track which orders are expanded by index
  
  // Parcel loading and caching
  orderParcelsCache: { [orderId: string]: ParcelDto[] } = {}; // Cache loaded parcels
  parcelLoadErrors: { [orderId: string]: string } = {}; // Track parcel loading errors
  isLoadingParcels: boolean = false;
  loadingParcelOrderIndex: number = -1;
  
  // Parcel pagination and filtering
  parcelSearchTerms: { [orderId: string]: string } = {}; // Search terms for each order
  parcelPageSizes: { [orderId: string]: number } = {}; // Page sizes for each order
  parcelCurrentPages: { [orderId: string]: number } = {}; // Current pages for each order
  filteredParcelsCache: { [orderId: string]: ParcelDto[] } = {}; // Filtered parcels cache
  
  // CSV export functionality
  isExporting: boolean = false;
  exportingOrderId: string | null = null;

  // === SIGNALR REAL-TIME STATE ===

  // Real-time order status updates
  realtimeOrderUpdates: Map<string, OrderStatusUpdate> = new Map();

  // Real-time scanning activity
  realtimeScanActivity: ScanActivityBroadcast[] = [];
  orderScanningProgress: Map<string, { scannedCount: number; completionPercentage: number; lastActivity: Date }> = new Map();

  // Active scanning indicators
  activeScanningOrders: Set<string> = new Set();
  scanningActivityFeed: { orderId: string; activity: string; timestamp: Date; user: string }[] = [];

  // Real-time UI controls
  showRealtimeUpdates = true;
  showScanningActivity = true;
  autoRefreshEnabled = true;
  autoRefreshInterval = 30000; // 30 seconds

  // SignalR connection status
  signalRConnected = false;
  lastUpdateTimestamp: Date | null = null;

  // Filter and pagination parameters
  filterParams: GetOrdersParams = {
    page: 1,
    pageSize: 10, // Default page size
    status: undefined, // Or a default status like 'OrderReceived'
    searchTerm: undefined,
    dateFrom: undefined,
    dateTo: undefined
  };

  // For status dropdown
  availableStatuses: WorkflowStatus[] = [
    'OrderReceived', 'InfoReceived', 'ManifestPending', 'LoadingDataReady',
    'LoadingSent', 'Shipped', 'Arrived', 'CustomsProcessing', 'CustomsCleared',
    'ReadyForDelivery', 'Delivering', 'Delivered', 'Completed', 'Cancelled', 'Error'
  ];

  constructor(
    private orderService: OrderService,
    private signalRService: SignalRService,
    private router: Router
  ) {
    // Initialize default parcel pagination settings
    this.initializeParcelSettings();
  }
  
  private initializeParcelSettings(): void {
    // Default settings for parcel display
    // These will be set per order when needed
  }

  ngOnInit(): void {
    this.loadOrders();
    this.checkForHighlightedOrders();
    this.initializeSignalRSubscriptions();
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupSignalRSubscriptions();
  }

  /**
   * Check for orders to highlight from session storage
   */
  checkForHighlightedOrders(): void {
    const highlightData = sessionStorage.getItem('highlightOrders');
    if (highlightData) {
      try {
        const data = JSON.parse(highlightData);
        const now = Date.now();
        
        // Check if highlight period hasn't expired
        if (data.timestamp && data.duration && (now - data.timestamp) < data.duration) {
          this.highlightedOrderIds = new Set(data.orderIds || []);
          
          // Clear highlights after remaining duration
          const remainingTime = data.duration - (now - data.timestamp);
          setTimeout(() => {
            this.highlightedOrderIds.clear();
            sessionStorage.removeItem('highlightOrders');
          }, remainingTime);
        } else {
          // Expired, remove from storage
          sessionStorage.removeItem('highlightOrders');
        }
      } catch (error) {
        console.error('Error parsing highlight data:', error);
        sessionStorage.removeItem('highlightOrders');
      }
    }
  }

  /**
   * Check if an order should be highlighted
   */
  isOrderHighlighted(orderId: string): boolean {
    return this.highlightedOrderIds.has(orderId);
  }

  loadOrders(): void {
    this.clearCache(); // Clear cached data when reloading orders
    
    this.isLoading = true;
    this.errorMessage = null;
    this.orderService.getOrders(this.filterParams).subscribe({
      next: (response) => {
        this.ordersResponse = response;
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error fetching orders:', err);
        this.errorMessage = `Failed to load orders. Status: ${err.status}`;
        this.isLoading = false;
        this.ordersResponse = null; // Clear previous data on error
      }
    });
  }

  onPageChange(newPage: number): void {
    if (newPage > 0 && (!this.ordersResponse || newPage <= this.ordersResponse.totalPages)) {
      this.filterParams.page = newPage;
      this.loadOrders();
    }
  }

  onPageSizeChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.filterParams.pageSize = +selectElement.value;
    this.filterParams.page = 1; // Reset to first page when page size changes
    this.loadOrders();
  }

  applyFilters(): void {
    this.filterParams.page = 1; // Reset to first page when filters change
    this.loadOrders();
  }

  clearFilters(): void {
    this.filterParams = {
      page: 1,
      pageSize: this.filterParams.pageSize, // Keep current page size
      status: undefined,
      searchTerm: '', // Clear search term
      dateFrom: undefined,
      dateTo: undefined
    };
    this.loadOrders();
  }

  viewOrderDetails(orderId: string): void {
    if (!orderId) {
      console.error('Invalid order ID provided:', orderId);
      return;
    }
    
    this.router.navigate(['/dashboard/orders', orderId]);
  }

  // Helper for creating an array for pagination buttons
  getPagesArray(): number[] {
    if (!this.ordersResponse || this.ordersResponse.totalPages <= 0) {
      return [];
    }
    return Array(this.ordersResponse.totalPages).fill(0).map((x, i) => i + 1);
  }
  
  // ===== EXPANDABLE ROWS FUNCTIONALITY =====
  
  /**
   * Toggle expansion state of an order row
   */
  toggleOrderExpansion(orderIndex: number): void {
    if (this.expandedOrders.has(orderIndex)) {
      this.expandedOrders.delete(orderIndex);
    } else {
      this.expandedOrders.add(orderIndex);
    }
  }
  
  /**
   * Check if an order is expanded
   */
  isOrderExpanded(orderIndex: number): boolean {
    return this.expandedOrders.has(orderIndex);
  }
  
  // ===== PARCEL LOADING FUNCTIONALITY =====
  
  /**
   * Load parcels for a specific order
   */
  async loadOrderParcels(orderId: string, orderIndex: number): Promise<void> {
    if (this.orderParcelsCache[orderId]) {
      return; // Already loaded
    }
    
    this.isLoadingParcels = true;
    this.loadingParcelOrderIndex = orderIndex;
    delete this.parcelLoadErrors[orderId];
    
    try {
      const orderDetail = await this.orderService.getOrderDetails(orderId).toPromise();
      if (orderDetail?.parcels) {
        this.orderParcelsCache[orderId] = orderDetail.parcels;
        this.initializeParcelPaginationForOrder(orderId, orderDetail.parcels.length);
        this.updateFilteredParcels(orderId);
      } else {
        this.parcelLoadErrors[orderId] = 'No parcels found for this order';
      }
    } catch (error) {
      console.error('Error loading parcels:', error);
      this.parcelLoadErrors[orderId] = error instanceof HttpErrorResponse ? 
        `Failed to load parcels: ${error.message}` : 
        'Failed to load parcels. Please try again.';
    } finally {
      this.isLoadingParcels = false;
      this.loadingParcelOrderIndex = -1;
    }
  }
  
  /**
   * Initialize parcel pagination settings for a specific order
   */
  private initializeParcelPaginationForOrder(orderId: string, parcelCount: number): void {
    if (!this.parcelPageSizes[orderId]) {
      // Set default page size based on parcel count
      if (parcelCount > 100) {
        this.parcelPageSizes[orderId] = 25;
      } else if (parcelCount > 50) {
        this.parcelPageSizes[orderId] = 50;
      } else {
        this.parcelPageSizes[orderId] = -1; // Show all
      }
    }
    
    if (!this.parcelCurrentPages[orderId]) {
      this.parcelCurrentPages[orderId] = 1;
    }
    
    if (!this.parcelSearchTerms[orderId]) {
      this.parcelSearchTerms[orderId] = '';
    }
  }
  
  // ===== PARCEL FILTERING AND PAGINATION =====
  
  /**
   * Handle parcel search input
   */
  onParcelSearch(orderId: string): void {
    this.parcelCurrentPages[orderId] = 1; // Reset to first page
    this.updateFilteredParcels(orderId);
  }
  
  /**
   * Handle parcel page size change
   */
  onParcelPageSizeChange(orderId: string): void {
    this.parcelCurrentPages[orderId] = 1; // Reset to first page
    this.updateFilteredParcels(orderId);
  }
  
  /**
   * Update filtered parcels cache
   */
  private updateFilteredParcels(orderId: string): void {
    const allParcels = this.orderParcelsCache[orderId];
    if (!allParcels) {
      this.filteredParcelsCache[orderId] = [];
      return;
    }
    
    const searchTerm = this.parcelSearchTerms[orderId]?.toLowerCase() || '';
    
    if (!searchTerm) {
      this.filteredParcelsCache[orderId] = allParcels;
    } else {
      this.filteredParcelsCache[orderId] = allParcels.filter(parcel =>
        parcel.trackingNumber?.toLowerCase().includes(searchTerm) ||
        parcel.Receiver_info?.receiver_name?.toLowerCase().includes(searchTerm) ||
        parcel.Receiver_info?.receiver_address?.toLowerCase().includes(searchTerm)
      );
    }
  }
  
  /**
   * Get filtered parcels for an order
   */
  getFilteredParcels(orderId: string): ParcelDto[] {
    return this.filteredParcelsCache[orderId] || [];
  }
  
  /**
   * Get paginated parcels for display
   */
  getPaginatedParcels(orderId: string): ParcelDto[] {
    const filteredParcels = this.getFilteredParcels(orderId);
    const pageSize = this.parcelPageSizes[orderId];
    const currentPage = this.parcelCurrentPages[orderId] || 1;
    
    if (pageSize === -1) {
      return filteredParcels; // Show all
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return filteredParcels.slice(startIndex, endIndex);
  }
  
  /**
   * Get current page for parcels
   */
  getCurrentParcelPage(orderId: string): number {
    return this.parcelCurrentPages[orderId] || 1;
  }
  
  /**
   * Get total pages for parcels
   */
  getTotalParcelPages(orderId: string): number {
    const filteredParcels = this.getFilteredParcels(orderId);
    const pageSize = this.parcelPageSizes[orderId];
    
    if (pageSize === -1 || filteredParcels.length === 0) {
      return 1;
    }
    
    return Math.ceil(filteredParcels.length / pageSize);
  }
  
  /**
   * Navigate to specific parcel page
   */
  goToParcelPage(orderId: string, page: number): void {
    const totalPages = this.getTotalParcelPages(orderId);
    if (page < 1 || page > totalPages) {
      return;
    }
    
    this.parcelCurrentPages[orderId] = page;
  }
  
  /**
   * Get page numbers for parcel pagination
   */
  getParcelPageNumbers(orderId: string): number[] {
    const totalPages = this.getTotalParcelPages(orderId);
    const currentPage = this.getCurrentParcelPage(orderId);
    
    // Show max 5 page numbers around current page
    const maxPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(totalPages, startPage + maxPages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }
  
  /**
   * TrackBy function for parcel list performance
   */
  trackParcelBy(index: number, parcel: ParcelDto): string {
    return parcel.trackingNumber || index.toString();
  }
  
  // ===== CSV EXPORT FUNCTIONALITY =====
  
  /**
   * Export parcels to CSV
   */
  async exportOrderParcels(orderId: string, shopeeOrderNo: string): Promise<void> {
    if (this.isExporting) {
      return;
    }
    
    this.isExporting = true;
    this.exportingOrderId = orderId;
    
    try {
      let parcels = this.orderParcelsCache[orderId];
      
      // Load parcels if not cached
      if (!parcels) {
        const orderDetail = await this.orderService.getOrderDetails(orderId).toPromise();
        parcels = orderDetail?.parcels || [];
        this.orderParcelsCache[orderId] = parcels;
      }
      
      if (parcels.length === 0) {
        alert('No parcels to export for this order.');
        return;
      }
      
      // Generate CSV content
      const csvContent = this.generateParcelsCsv(parcels, shopeeOrderNo);
      
      // Download CSV
      this.downloadCsv(csvContent, `${shopeeOrderNo}_parcels_${new Date().toISOString().split('T')[0]}.csv`);
      
    } catch (error) {
      console.error('Error exporting parcels:', error);
      alert('Failed to export parcels. Please try again.');
    } finally {
      this.isExporting = false;
      this.exportingOrderId = null;
    }
  }
  
  /**
   * Generate CSV content from parcels
   */
  private generateParcelsCsv(parcels: ParcelDto[], shopeeOrderNo: string): string {
    const headers = [
      'Shopee Order No',
      'Tracking Number',
      'Weight (kg)',
      'Length (cm)',
      'Width (cm)', 
      'Height (cm)',
      'Receiver Name',
      'Receiver Address',
      'Receiver City',
      'Receiver State',
      'Receiver Postal Code',
      'Receiver Phone'
    ];
    
    const rows = parcels.map(parcel => [
      shopeeOrderNo,
      parcel.trackingNumber || '',
      parcel.weight || '',
      parcel.length || '',
      parcel.width || '',
      parcel.height || '',
      parcel.Receiver_info?.receiver_name || '',
      parcel.Receiver_info?.receiver_address || '',
      parcel.Receiver_info?.receiver_city || '',
      parcel.Receiver_info?.receiver_state || '',
      parcel.Receiver_info?.receiver_postalCode || '',
      parcel.Receiver_info?.receiver_phone || ''
    ]);
    
    const csvRows = [headers, ...rows];
    return csvRows.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  }
  
  /**
   * Download CSV file
   */
  private downloadCsv(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
  
  // ===== ENHANCED ERROR HANDLING =====
  
  /**
   * Clear all cached data and reset state
   */
  clearCache(): void {
    this.orderParcelsCache = {};
    this.filteredParcelsCache = {};
    this.parcelLoadErrors = {};
    this.expandedOrders.clear();
    this.parcelSearchTerms = {};
    this.parcelPageSizes = {};
    this.parcelCurrentPages = {};
  }

  // === SIGNALR REAL-TIME INTEGRATION METHODS ===

  /**
   * Initialize SignalR subscriptions for real-time order updates
   */
  private initializeSignalRSubscriptions(): void {
    // Monitor SignalR connection status
    this.signalRService.connectionStatus$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(status => {
      this.signalRConnected = status.isConnected;
    });

    // Subscribe to all order status updates
    this.signalRService.subscribeToAllOrderStatusUpdates().pipe(
      takeUntil(this.destroy$)
    ).subscribe(statusUpdate => {
      this.handleRealtimeOrderStatusUpdate(statusUpdate);
    });

    // Subscribe to all scanning activity
    this.signalRService.subscribeToAllScanningEvents().pipe(
      takeUntil(this.destroy$)
    ).subscribe(scanActivity => {
      this.handleRealtimeScanActivity(scanActivity);
    });
  }

  /**
   * Handle real-time order status updates
   */
  private handleRealtimeOrderStatusUpdate(statusUpdate: OrderStatusUpdate): void {
    this.lastUpdateTimestamp = new Date();

    // Store the update
    this.realtimeOrderUpdates.set(statusUpdate.orderId, statusUpdate);

    // Update the order in our current list if it exists
    if (this.ordersResponse?.items) {
      const orderIndex = this.ordersResponse.items.findIndex(order => order.uniqueId === statusUpdate.orderId);

      if (orderIndex !== -1) {
        // Update the order status in the list
        this.ordersResponse.items[orderIndex].status = statusUpdate.newStatus as WorkflowStatus;

        // Highlight the updated order briefly
        this.highlightedOrderIds.add(statusUpdate.orderId);
        setTimeout(() => {
          this.highlightedOrderIds.delete(statusUpdate.orderId);
        }, 3000);

        // Add to activity feed
        this.addToActivityFeed(statusUpdate.orderId,
          `Status updated: ${statusUpdate.previousStatus} → ${statusUpdate.newStatus}`,
          'System');
      }
    }

    // Show real-time notification if enabled
    if (this.showRealtimeUpdates) {
      this.showRealtimeNotification(
        `Order ${statusUpdate.orderId} status updated to ${statusUpdate.newStatus}`,
        'success'
      );
    }
  }

  /**
   * Handle real-time scanning activity
   */
  private handleRealtimeScanActivity(scanActivity: ScanActivityBroadcast): void {
    this.lastUpdateTimestamp = new Date();

    // Add to scanning activity feed
    this.realtimeScanActivity.unshift(scanActivity);
    if (this.realtimeScanActivity.length > 50) {
      this.realtimeScanActivity = this.realtimeScanActivity.slice(0, 50);
    }

    // Track which orders have active scanning
    if (scanActivity.orderId) {
      this.activeScanningOrders.add(scanActivity.orderId);

      // Update scanning progress for the order
      const currentProgress = this.orderScanningProgress.get(scanActivity.orderId!) || {
        scannedCount: 0,
        completionPercentage: 0,
        lastActivity: new Date()
      };

      if (scanActivity.success) {
        currentProgress.scannedCount++;
        currentProgress.lastActivity = scanActivity.scanTime;
        this.orderScanningProgress.set(scanActivity.orderId!, currentProgress);
      }

      // Add to activity feed
      this.addToActivityFeed(
        scanActivity.orderId!,
        `${scanActivity.scannerUserName} scanned ${scanActivity.barcode}`,
        scanActivity.scannerUserName
      );

      // Remove from active scanning after inactivity
      setTimeout(() => {
        const lastActivity = this.orderScanningProgress.get(scanActivity.orderId!)?.lastActivity;
        if (lastActivity && (new Date().getTime() - lastActivity.getTime()) > 60000) { // 1 minute
          this.activeScanningOrders.delete(scanActivity.orderId!);
        }
      }, 60000);
    }

    // Show notification for scanning activity if enabled
    if (this.showScanningActivity && scanActivity.success) {
      this.showRealtimeNotification(
        `${scanActivity.scannerUserName} scanned carton for order ${scanActivity.orderId || 'unknown'}`,
        'info'
      );
    }
  }

  /**
   * Add activity to the feed
   */
  private addToActivityFeed(orderId: string, activity: string, user: string): void {
    this.scanningActivityFeed.unshift({
      orderId,
      activity,
      timestamp: new Date(),
      user
    });

    // Limit activity feed size
    if (this.scanningActivityFeed.length > 20) {
      this.scanningActivityFeed = this.scanningActivityFeed.slice(0, 20);
    }
  }

  /**
   * Setup auto-refresh functionality
   */
  private setupAutoRefresh(): void {
    if (!this.autoRefreshEnabled) return;

    interval(this.autoRefreshInterval).pipe(
      takeUntil(this.destroy$),
      filter(() => this.autoRefreshEnabled && this.signalRConnected)
    ).subscribe(() => {
      // Soft refresh - only update if we have real-time updates
      if (this.realtimeOrderUpdates.size > 0 || this.realtimeScanActivity.length > 0) {
        this.refreshOrderList();
      }
    });
  }

  /**
   * Refresh order list without losing current state
   */
  private refreshOrderList(): void {
    const currentExpandedOrders = new Set(this.expandedOrders);

    this.orderService.getOrders(this.filterParams).subscribe({
      next: (response) => {
        this.ordersResponse = response;

        // Restore expanded state
        this.expandedOrders = currentExpandedOrders;

        // Update scanning progress from real-time data
        this.updateOrdersWithRealtimeData();
      },
      error: (err) => {
        console.warn('Auto-refresh failed:', err);
      }
    });
  }

  /**
   * Update orders with real-time data
   */
  private updateOrdersWithRealtimeData(): void {
    if (!this.ordersResponse?.items) return;

    this.ordersResponse.items.forEach(order => {
      // Apply real-time status updates
      const statusUpdate = this.realtimeOrderUpdates.get(order.uniqueId);
      if (statusUpdate) {
        order.status = statusUpdate.newStatus as WorkflowStatus;
      }

      // Add scanning progress indicators
      const scanProgress = this.orderScanningProgress.get(order.uniqueId);
      if (scanProgress) {
        // Add scanning progress data to order display
        (order as any).realtimeScanProgress = scanProgress;
      }
    });
  }

  /**
   * Show real-time notification
   */
  private showRealtimeNotification(message: string, type: 'success' | 'info' | 'warning' = 'info'): void {
    // Simple console notification for now - could be enhanced with toast notifications
    console.log(`🔴 REAL-TIME: ${message}`);

    // Could integrate with a notification service here
    // this.notificationService.show(message, type);
  }

  /**
   * Cleanup SignalR subscriptions
   */
  private async cleanupSignalRSubscriptions(): Promise<void> {
    try {
      await this.signalRService.unsubscribeFromScanningEvents();
    } catch (error) {
      console.warn('Error cleaning up SignalR subscriptions:', error);
    }
  }

  // === SIGNALR UI GETTERS ===

  get hasRealtimeUpdates(): boolean {
    return this.realtimeOrderUpdates.size > 0 || this.realtimeScanActivity.length > 0;
  }

  get activeScanningOrdersCount(): number {
    return this.activeScanningOrders.size;
  }

  get recentScanningActivity(): ScanActivityBroadcast[] {
    return this.realtimeScanActivity.slice(0, 5);
  }

  get recentActivityFeed(): { orderId: string; activity: string; timestamp: Date; user: string }[] {
    return this.scanningActivityFeed.slice(0, 10);
  }

  get connectionStatusText(): string {
    return this.signalRConnected ? 'Connected' : 'Disconnected';
  }

  get connectionStatusClass(): string {
    return this.signalRConnected ? 'text-success' : 'text-danger';
  }

  /**
   * Check if an order has active scanning
   */
  isOrderBeingScanned(orderId: string): boolean {
    return this.activeScanningOrders.has(orderId);
  }

  /**
   * Get scanning progress for an order
   */
  getOrderScanningProgress(orderId: string): { scannedCount: number; completionPercentage: number; lastActivity: Date } | null {
    return this.orderScanningProgress.get(orderId) || null;
  }

  /**
   * Get time since last update
   */
  getTimeSinceLastUpdate(): string {
    if (!this.lastUpdateTimestamp) return 'Never';

    const now = new Date();
    const diff = now.getTime() - this.lastUpdateTimestamp.getTime();
    const seconds = Math.floor(diff / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  }

  // === DS TOKEN HELPERS ===

  getOrderStatusVariant(status: WorkflowStatus | string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default' {
    switch (status) {
      case 'Completed': case 'Delivered': return 'success';
      case 'OrderReceived': return 'default';
      case 'InfoReceived': case 'Arrived': return 'info';
      case 'ManifestPending': case 'LoadingDataReady': case 'CustomsProcessing': return 'warning';
      case 'Cancelled': case 'Error': return 'danger';
      default: return 'neutral';
    }
  }

  // === SIGNALR UI ACTIONS ===

  toggleRealtimeUpdates(): void {
    this.showRealtimeUpdates = !this.showRealtimeUpdates;
  }

  toggleScanningActivity(): void {
    this.showScanningActivity = !this.showScanningActivity;
  }

  toggleAutoRefresh(): void {
    this.autoRefreshEnabled = !this.autoRefreshEnabled;

    if (this.autoRefreshEnabled) {
      this.setupAutoRefresh();
    }
  }

  clearRealtimeData(): void {
    this.realtimeOrderUpdates.clear();
    this.realtimeScanActivity = [];
    this.orderScanningProgress.clear();
    this.activeScanningOrders.clear();
    this.scanningActivityFeed = [];
    this.lastUpdateTimestamp = null;
  }

  forceRefresh(): void {
    this.loadOrders();
  }

  forceSignalRReconnect(): void {
    this.signalRService.forceReconnect();
  }
}



